import { css, keyframes } from 'styled-components'
import type {
  ButtonSize,
  ButtonVariant,
} from 'sutra/components/Button/button-types'
import { accents, brand, gray } from 'sutra/tokens/colors'

// Base UI renders a native `disabled` for a plain disabled button but
// `aria-disabled` (focusable) for a loading one, so every state selector
// has to cover both.
export const DISABLED = "&:is(:disabled, [aria-disabled='true'])"
const ENABLED = ':not(:disabled):not([aria-disabled="true"])'

export const sizeStyles: Record<ButtonSize, ReturnType<typeof css>> = {
  xs: css`
    font-size: 14px;
    line-height: 20px;
    height: 24px;
    padding: 0 8px;
    gap: 4px;
  `,
  sm: css`
    font-size: 14px;
    line-height: 20px;
    padding: 7px 14px;
    gap: 6px;
  `,
  md: css`
    font-size: 16px;
    line-height: 24px;
    padding: 10px 18px;
    gap: 8px;
  `,
  lg: css`
    font-size: 17px;
    line-height: 26px;
    padding: 13px 22px;
    gap: 8px;
  `,
}

// Icon-only buttons are square: width follows an explicit height (the border
// and line-height of each size, since padding is dropped).
export const squareSizes: Record<ButtonSize, string> = {
  xs: '24px',
  sm: '36px',
  md: '46px',
  lg: '54px',
}

export const variantStyles: Record<ButtonVariant, ReturnType<typeof css>> = {
  primary: css`
    background: ${accents.reflectionBlue[500]};
    color: ${brand.white};
    border-color: transparent;
    &:hover${ENABLED} {
      background: ${accents.reflectionBlue[600]};
    }
    &:active${ENABLED} {
      background: ${accents.reflectionBlue[700]};
      box-shadow: none;
    }
  `,
  secondary: css`
    background: ${brand.white};
    color: ${brand.stillNight[800]};
    border: 1px solid #d9d9d9;
    &:hover${ENABLED} {
      background: ${gray[100]};
      border-color: ${gray[300]};
    }
  `,
  tertiary: css`
    background: transparent;
    color: #414651;
    border-color: transparent;
    padding-left: 4px;
    padding-right: 4px;
    &:hover${ENABLED} {
      color: ${brand.stillNight[800]};
    }
  `,
  text: css`
    background: transparent;
    color: ${brand.stillNight[800]};
    border-color: transparent;
    box-shadow: none;
    &:hover${ENABLED} {
      background: ${gray[200]};
    }
  `,
  link: css`
    background: transparent;
    color: ${accents.reflectionBlue[500]};
    border-color: transparent;
    box-shadow: none;
    &:hover${ENABLED} {
      color: ${accents.reflectionBlue[600]};
      text-decoration: underline;
    }
  `,
  dark: css`
    background: ${brand.stillNight[800]};
    color: ${brand.white};
    border-color: transparent;
    &:hover${ENABLED} {
      background: ${brand.stillNight[950]};
    }
  `,
  askYogi: css`
    background: ${accents.reflectionBlue[500]};
    color: ${brand.white};
    border-color: transparent;
    font-weight: 600;
    letter-spacing: -0.005em;
    &:hover${ENABLED} {
      background: ${accents.reflectionBlue[600]};
    }
  `,
  destructive: css`
    background: ${accents.crimsonBloom[500]};
    color: ${brand.white};
    border-color: transparent;
    &:hover${ENABLED} {
      background: ${accents.crimsonBloom[600]};
    }
    &:active${ENABLED} {
      background: ${accents.crimsonBloom[700]};
      box-shadow: none;
    }
  `,
  destructiveSecondary: css`
    background: ${brand.white};
    color: ${accents.crimsonBloom[500]};
    border: 1px solid #d9d9d9;
    &:hover${ENABLED} {
      background: ${accents.crimsonBloom[500]};
      color: ${brand.white};
      border-color: transparent;
    }
    &:active${ENABLED} {
      background: ${accents.crimsonBloom[600]};
      border-color: transparent;
      box-shadow: none;
    }
  `,
}

// `danger` only recolours the transparent variants; a danger primary/secondary
// is the `destructive` / `destructiveSecondary` variant instead.
export const dangerStyles: Partial<
  Record<ButtonVariant, ReturnType<typeof css>>
> = {
  tertiary: css`
    color: ${accents.crimsonBloom[500]};
    &:hover${ENABLED} {
      color: ${accents.crimsonBloom[600]};
    }
  `,
  text: css`
    color: ${accents.crimsonBloom[500]};
    &:hover${ENABLED} {
      background: ${accents.crimsonBloom[50]};
    }
  `,
  link: css`
    color: ${accents.crimsonBloom[500]};
    &:hover${ENABLED} {
      color: ${accents.crimsonBloom[600]};
    }
  `,
}

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

export const spinnerStyles = css`
  flex: none;
  width: 1em;
  height: 1em;
  box-sizing: border-box;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`
